import { useEffect, useRef, useState } from 'react';
import { appStatusSource, type AppStatusSnapshot, type CacheProgress } from '../lib/app-status-source';

interface UseServerStatusResult {
  warming: boolean;
  progress: CacheProgress | null;
  /** True on first load before we have any status */
  unknown: boolean;
}

const DEFAULT_PROGRESS: CacheProgress = {
  drivers: 0, circuits: 0, constructors: 0, seasonsLoaded: 0, totalSeasons: 76,
};

export function shouldPollServerStatus(status: { warming?: boolean } | null | undefined) {
  return Boolean(status?.warming);
}

/**
 * Polls `/api/data?type=status` every `interval` ms while the server is warming.
 * Stops polling once the cache is ready (warming === false).
 */
export function useServerStatus(interval = 3000): UseServerStatusResult {
  const [snapshot, setSnapshot] = useState<AppStatusSnapshot>(() => appStatusSource.getSnapshot());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    const syncSnapshot = () => {
      if (!mountedRef.current) return;
      setSnapshot(appStatusSource.getSnapshot());
    };

    const schedulePoll = (delay: number) => {
      timerRef.current = setTimeout(async () => {
        try {
          const status = await appStatusSource.load({ force: true });
          if (shouldPollServerStatus(status)) {
            schedulePoll(interval);
          }
        } catch {}
      }, delay);
    };

    const unsubscribe = appStatusSource.subscribe(syncSnapshot);

    void appStatusSource.load()
      .then((status) => {
        if (shouldPollServerStatus(status)) {
          schedulePoll(interval);
        }
      });

    return () => {
      mountedRef.current = false;
      unsubscribe();
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [interval]);

  return {
    warming: snapshot.status?.warming ?? false,
    progress: snapshot.status?.progress ?? (snapshot.unknown ? null : DEFAULT_PROGRESS),
    unknown: snapshot.unknown,
  };
}
